import {React, _} from '@api'
import {UNASSIGNED} from '@api/graphql'
import {useStores, VipLocalStore} from '@modules'
import {ISpecGroupByPdf} from '@modules/pdf'
import {GroupByGrouping, GroupTitleConfig, StoresInstance} from '@stores'
import {computed} from 'mobx'
import { SpecHexPdf, ProductCardLineItem, PDFComponents} from './VlpProductCard.styles'

const parseTitleField = ({field, prefix}: GroupTitleConfig, _source: any, spec?: ISpecGroupByPdf) => {
  let {catalog: {_fieldByName}, vlp} = useStores(),
    def = _fieldByName[field],
    value = _.isFunction(def?.group?.titleFor) ? def?.group?.titleFor(spec) : _.get(_source, field) ?? spec.title

  if (!value) {
    const {hierarchy: {parseHierarchyGroup, itemOfType, deepestFolderIndex, folderById}} = vlp

    const {type, folderDepth} = parseHierarchyGroup(field) ?? {}

    if (type) {
      const dynamicField = _.keys(_source).find(k => parseHierarchyGroup(k))
      if (dynamicField) {
        const hierarchyItemType = parseHierarchyGroup(dynamicField).type,
          hierarchyItem = itemOfType(hierarchyItemType, _.get(_source, dynamicField), null)

        if (hierarchyItem) {
          switch (type) {
            case 'plan': {
              value = hierarchyItem['plan']?.name
              break
            }

            case 'folder': {
              let folder = hierarchyItemType == 'folder' ? hierarchyItem : folderById[hierarchyItem['folder']['id']],
                level = hierarchyItem['level']

              for (let i = level !== undefined ? level - 1 : deepestFolderIndex; i > folderDepth; i--) {
                folder = folderById[folder?.['parent']?.id]
              }

              value = folder?.name

              break
            }

            case 'collection': {
              debugger
            }
          }
        }
      }
    }
  }

  return !_.isNull(value) && !_.isUndefined(value) ? _.isBoolean(value) ? String(value).replace(/true/g, 'Yes').replace(/false/g, 'No') : `${prefix || ''}${value}` : null
}

interface IVlpSpecCardPdfStore {
  spec: ISpecGroupByPdf,
  group?: GroupByGrouping,
  height?: number,
  width?: number,
  onlyTitle?: boolean,
  columnSize?: number
}

class VlpSpecCardPdfStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: IVlpSpecCardPdfStore) {
    super(stores)
  }

  @computed get group() {
    const {props: {group}, stores} = this, {groupBy: {firstGroup}} = stores
    return group ?? firstGroup
  }

  @computed get spec() {
    const {props} = this
    return props.spec
  }

  @computed get isCustomAttribute() {
    const {group: {fieldDefinition}} = this
    return fieldDefinition.field.startsWith('attributes')
  }

  @computed get groupName() {
    const {spec, group: {groupName}} = this
    return spec.groupName ?? groupName
  }

  @computed get showImage() {
    const {catalog: {pdfConfig}, groupName, isCustomAttribute} = this
    return !_.includes(pdfConfig?.types || [], groupName) || !isCustomAttribute
  }

  @computed get showPlaceholder() {
    const {showImage, isCustomAttribute, layout} = this
    return showImage && !isCustomAttribute && layout !== 'column'
  }

  @computed get titleText() {
    const {subtitleText, title, hasSubtitleEmpty, group: {fullTitle}, groupName, catalog: {labelForFieldName_currentCatalog}} = this,
      label = fullTitle ?? labelForFieldName_currentCatalog(groupName)
    let res = title
    if (hasSubtitleEmpty || title === subtitleText) {
      res = label
    }
    return res
  }

  @computed get title() {
      const {spec, strings, group: {fieldDefinition: {group, label}, hierarchyTypeGroup}} = this

      return group ? group?.title?.field ? (parseTitleField(group?.title, spec.keys, spec) ?? strings.noValue)
                                         : (group?.subtitle?.length || hierarchyTypeGroup || spec.title) ? spec.title
                                                                                                         : label
                   : label
  }


  @computed get subtitle() {
    const {spec, group: {def, subtitleFields, hierarchyTypeGroup}, isCustomAttribute, catalog: {labelFor}, strings} = this

    return spec.key === UNASSIGNED ? def?.type === 'Boolean' ? (def?.booleanLabels?.falseLabel ?? 'False')
                                                             : strings.unassigned
                                   : (subtitleFields.length || hierarchyTypeGroup) ? subtitleFields.map(f => parseTitleField(f, spec.keys, spec) ?? strings.noValue).filter(f => f).join(', ')
                                                                                   : isCustomAttribute ? labelFor(spec) : spec.subtitle ?? labelFor(spec)
  }

  @computed get hasSubtitleEmpty() {
    const {subtitle} = this
    return (_.isNil(subtitle) || _.isUndefined(subtitle) || _.isEmpty(subtitle))
  }

  @computed get subtitleText() {
    const {layout, subtitle, hasSubtitleEmpty, title, props: {onlyTitle}, spec, group, group: {fieldDefinition}} = this
    let res = subtitle
    if (layout === 'column' && (fieldDefinition.group?.subtitle || (onlyTitle && !hasSubtitleEmpty)) && !_.isEmpty(group) && title !== subtitle) {
      res = `${title}, ${subtitle}`
    }
    if (layout === 'column' && spec?.count && onlyTitle) {
      res = `${res} (${spec?.count})`
    }
    if (hasSubtitleEmpty || title === subtitle) {
      res = spec.title ?? title
    }
    return res
  }

  @computed get layout() {
    const {pdf: {config}} = this
    return config.layout
  }

  @computed get specStyle() {
    const {pdf: {config: {group}}} = this
    return group?.spec?.style ?? {}
  }

  @computed get height() {
    const {props} = this
    return props.height
  }

  @computed get width() {
    const {props} = this
    return props.width
  }

  @computed get isImage() {
    const {layout, imageUrl, isHex} = this
    return layout !== 'column' && !_.isEmpty(imageUrl) && !isHex
  }

  @computed get style() {
    const {height, specStyle, width, isImage} = this

    return height ? {...specStyle, height: isImage ? height : height} : width ? {...specStyle, width} : {...specStyle}
  }

  @computed get keys() {
    const {spec} = this
    return spec.keys
  }

  @computed get groupKey() {
    const {keys} = this
    return _.first(_.keys(keys))
  }

  @computed get specInstance() {
    const {spec: {items: {items}}, groupKey} = this
    return _.get(items.find(f => _.has(f, groupKey)), groupKey)
  }

  @computed get imagesRaw() {
    const {specInstance} = this
    return specInstance?.images ?? specInstance
  }

  @computed get imageUrl() {
    const {imagesRaw, catalog: {imageUrlFor}} = this,
      image = Array.isArray(imagesRaw) ? _.first(imagesRaw) : imagesRaw

    return imageUrlFor(image, {c8Size: 'smallImage'}) ?? ''
  }

  @computed get isHex() {
    const {imageUrl} = this
    return imageUrl?.startsWith('#')
  }

  @computed get items_length() {
    const {spec: {limited, children, items: {items}}} = this,
      children_count = limited ? limited : children ? children.reduce((acc, c) => acc + c.items.items.length, 0) : 0

    return children_count ? children_count : items.length
  }

  @computed get total_count() {
    const {items_length, group, spec: {doc_count, items: {total: {value}}}} = this

    return !_.isEmpty(group) ? value ?? doc_count : items_length
  }

  @computed get hex() {
    const {spec} = this
    return spec.hex
  }

  @computed get hideSubtitle() {
    const {group: {fieldDefinition}} = this

    return fieldDefinition?.group?.hideSubtitle
  }

  @computed get onlyTitle() {
    const {props} = this
    return props.onlyTitle
  }

  @computed get columnSize() {
    const {props} = this

    return props.columnSize
  }

}

export const VlpSpecCardPdf: React.FunctionComponent<IVlpSpecCardPdfStore> = (props) => {
  const stores = useStores(),
    {
      onlyTitle, columnSize, subtitleText, showPlaceholder, titleText, isCustomAttribute,
      style, total_count, items_length, isImage, isHex, imageUrl, hex,
      hideSubtitle, pdf: {config: {imageRatio, columns, showCount}, imageHeight, cardWidth, cardMargin}
    } = new VlpSpecCardPdfStore(stores, props),
    {SpecImageContainer, SpecTitleView, ProductImage, ImagePlaceholder, DetailsContainer} = PDFComponents(stores)

  return (
    <SpecTitleView style={style} columnSize={columnSize ?? 0} width={cardWidth} margin={cardMargin} imageRatio={imageRatio} columns={columns}>
      {(hex || showPlaceholder) && <SpecImageContainer height={imageHeight} imageRatio={imageRatio} columns={columns}>
        {hex ? <SpecHexPdf hex={hex}/> :
         isImage && imageUrl ? <ProductImage height={imageHeight} width={cardWidth} src={imageUrl ?? ''}/> :
         showPlaceholder && <ImagePlaceholder/>}
	  </SpecImageContainer>}
      <DetailsContainer hasImage={isHex || isImage || showPlaceholder} imageRatio={imageRatio} columns={columns} style={{border: 0}}>
        {!onlyTitle && <ProductCardLineItem
			columns={columns}
			rightTextStyle={{fontFamily: isCustomAttribute ? 'Roboto Bold' : undefined, maxLines: 2, fontSize: 7, maxHeight: 22, textOverflow: 'visible'}}
			right={titleText ? (titleText.toString()) : '--'}/>}
        {<ProductCardLineItem rightTextStyle={{maxLines: 3, maxHeight: 33, fontFamily: 'Roboto Regular'}} right={hideSubtitle ? null : _.toString(subtitleText) ? (subtitleText.toString()) : '--'}/>}
        {!onlyTitle && showCount && total_count ? <ProductCardLineItem right={`(${total_count != items_length && items_length > 0 && total_count > 0 ? `${_.toString(items_length)} / ` : ''}${total_count?.toString()})`}/> : null}
      </DetailsContainer>
    </SpecTitleView>
  )
}



// WEBPACK FOOTER //
// ./src/modules/pdf/templates/vlp/VlpSpecCardPdf.tsx